/**
 * usePointAssociation - Hook para manejar asociación de cualquier entidad punto
 *
 * @remarks
 * Hook genérico que funciona para cualquier tipo de Point Entity
 * (trees, stops, lights, etc.). Soporta conexiones múltiples.
 *
 * @see ARQUITECTURA_ASOCIACION_PUNTO_RED.md
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  pointAssociationApi,
  type NetworkPointAssociation,
  type AssociationCandidate,
} from '../services/point-association-api.service';
import { getPointEntityConfig } from '../config/point-entities.config';

export interface UsePointAssociationResult {
  /** Asociación primaria (null si no existe o está cargando) */
  association: NetworkPointAssociation | null;
  /** Todas las asociaciones (array vacío si no hay) */
  allAssociations: NetworkPointAssociation[];
  /** Candidatos para conexión manual */
  candidates: AssociationCandidate[];
  /** Si está cargando */
  isLoading: boolean;
  /** Si está cargando candidatos */
  isLoadingCandidates: boolean;
  /** Error si hubo alguno */
  error: Error | null;
  /** Si permite múltiples conexiones */
  allowsMultipleConnections: boolean;
  /** Recalcular la asociación primaria */
  recalculate: () => Promise<NetworkPointAssociation | null>;
  /** Bloquear la asociación primaria (legacy) */
  lock: () => Promise<NetworkPointAssociation | null>;
  /** Desbloquear la asociación primaria (legacy) */
  unlock: () => Promise<NetworkPointAssociation | null>;
  /** Bloquear una conexión específica */
  lockConnection: (connectionId: string) => Promise<NetworkPointAssociation | null>;
  /** Desbloquear una conexión específica */
  unlockConnection: (connectionId: string) => Promise<NetworkPointAssociation | null>;
  /** Agregar conexión manual */
  addConnection: (target: { type: 'edge' | 'node'; id: string }) => Promise<NetworkPointAssociation | null>;
  /** Reemplazar la conexión primaria */
  replacePrimary: (target: { type: 'edge' | 'node'; id: string }) => Promise<NetworkPointAssociation | null>;
  /** Eliminar una conexión */
  removeConnection: (connectionId: string) => Promise<void>;
  /** Establecer conexión como primaria */
  setPrimary: (connectionId: string) => Promise<NetworkPointAssociation | null>;
  /** Cargar candidatos para conexión manual */
  loadCandidates: (options?: { maxDistance?: number; includeNodes?: boolean }) => Promise<void>;
  /** Limpiar candidatos */
  clearCandidates: () => void;
  /** Recargar todas las asociaciones desde el servidor */
  refresh: () => Promise<void>;
}

/**
 * Hook para manejar asociación de cualquier entidad punto
 *
 * @param projectId - ID del proyecto (null si no hay proyecto seleccionado)
 * @param entityType - Tipo de entidad (e.g., 'tree', 'transit_stop')
 * @param entityId - ID de la entidad (null si no hay entidad seleccionada)
 * @param coordinates - Coordenadas de la entidad [lng, lat] (para candidatos)
 *
 * @example
 * ```tsx
 * const {
 *   association,
 *   allAssociations,
 *   addConnection,
 *   removeConnection,
 *   setPrimary
 * } = usePointAssociation(projectId, 'tree', selectedTree?.id, selectedTree?.coordinates);
 *
 * return (
 *   <div>
 *     {allAssociations.map(assoc => (
 *       <ConnectionItem
 *         key={assoc.id}
 *         association={assoc}
 *         onSetPrimary={() => setPrimary(assoc.id)}
 *         onRemove={() => removeConnection(assoc.id)}
 *       />
 *     ))}
 *   </div>
 * );
 * ```
 */
export function usePointAssociation(
  projectId: string | null,
  entityType: string,
  entityId: string | null,
  coordinates?: [number, number],
): UsePointAssociationResult {
  const [allAssociations, setAllAssociations] = useState<NetworkPointAssociation[]>([]);
  const [candidates, setCandidates] = useState<AssociationCandidate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Obtener config para saber si permite múltiples conexiones
  const config = useMemo(() => getPointEntityConfig(entityType), [entityType]);
  const allowsMultipleConnections = config?.allowMultipleConnections ?? false;

  // Asociación primaria (la primera que tenga isPrimary=true, o la primera del array)
  const association = useMemo(() => {
    if (allAssociations.length === 0) return null;
    const primary = allAssociations.find((a) => a.isPrimary);
    return primary ?? allAssociations[0];
  }, [allAssociations]);

  /**
   * Carga todas las asociaciones desde el servidor
   */
  const loadAssociations = useCallback(async () => {
    if (!projectId || !entityId) {
      setAllAssociations([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Usar endpoint /all para obtener todas las conexiones
      const results = await pointAssociationApi.getAllAssociations(projectId, entityType, entityId);
      setAllAssociations(results);
    } catch (err) {
      console.error('[usePointAssociation] Error loading associations:', err);
      // Fallback: intentar con getAssociation (una sola)
      try {
        const single = await pointAssociationApi.getAssociation(projectId, entityType, entityId);
        setAllAssociations(single ? [single] : []);
      } catch {
        setError(err instanceof Error ? err : new Error('Unknown error'));
        setAllAssociations([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [projectId, entityType, entityId]);

  const replacePrimary = useCallback(async (
    target: { type: 'edge' | 'node'; id: string },
  ): Promise<NetworkPointAssociation | null> => {
    if (!projectId || !entityId || !coordinates) return null;

    setIsLoading(true);
    setError(null);

    try {
      const result = await pointAssociationApi.replacePrimaryAssociation(
        projectId,
        entityType,
        entityId,
        target,
        coordinates,
      );
      await loadAssociations();
      return result;
    } catch (err) {
      console.error('[usePointAssociation] Error replacing primary association:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [projectId, entityType, entityId, coordinates, loadAssociations]);

  /**
   * Cargar al montar y cuando cambian las dependencias
   */
  useEffect(() => {
    void loadAssociations();
  }, [loadAssociations]);

  /**
   * Recalcular la asociación
   */
  const recalculate = useCallback(async (): Promise<NetworkPointAssociation | null> => {
    if (!projectId || !entityId) return null;

    setIsLoading(true);
    setError(null);

    try {
      const result = await pointAssociationApi.calculate(projectId, entityType, entityId);
      // Recargar todas las asociaciones
      await loadAssociations();
      return result;
    } catch (err) {
      console.error('[usePointAssociation] Error recalculating:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [projectId, entityType, entityId, loadAssociations]);

  /**
   * Bloquear la asociación primaria (legacy)
   */
  const lock = useCallback(async (): Promise<NetworkPointAssociation | null> => {
    if (!projectId || !entityId) return null;

    setIsLoading(true);
    setError(null);

    try {
      const result = await pointAssociationApi.lock(projectId, entityType, entityId);
      await loadAssociations();
      return result;
    } catch (err) {
      console.error('[usePointAssociation] Error locking:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [projectId, entityType, entityId, loadAssociations]);

  /**
   * Desbloquear la asociación primaria (legacy)
   */
  const unlock = useCallback(async (): Promise<NetworkPointAssociation | null> => {
    if (!projectId || !entityId) return null;

    setIsLoading(true);
    setError(null);

    try {
      const result = await pointAssociationApi.unlock(projectId, entityType, entityId);
      await loadAssociations();
      return result;
    } catch (err) {
      console.error('[usePointAssociation] Error unlocking:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [projectId, entityType, entityId, loadAssociations]);

  /**
   * Bloquear una conexión específica
   */
  const lockConnection = useCallback(async (connectionId: string): Promise<NetworkPointAssociation | null> => {
    if (!projectId || !entityId) return null;

    setIsLoading(true);
    setError(null);

    try {
      const result = await pointAssociationApi.lockConnection(projectId, entityType, entityId, connectionId);
      await loadAssociations();
      return result;
    } catch (err) {
      console.error('[usePointAssociation] Error locking connection:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [projectId, entityType, entityId, loadAssociations]);

  /**
   * Desbloquear una conexión específica
   */
  const unlockConnection = useCallback(async (connectionId: string): Promise<NetworkPointAssociation | null> => {
    if (!projectId || !entityId) return null;

    setIsLoading(true);
    setError(null);

    try {
      const result = await pointAssociationApi.unlockConnection(projectId, entityType, entityId, connectionId);
      await loadAssociations();
      return result;
    } catch (err) {
      console.error('[usePointAssociation] Error unlocking connection:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [projectId, entityType, entityId, loadAssociations]);

  /**
   * Agregar conexión manual
   */
  const addConnection = useCallback(async (
    target: { type: 'edge' | 'node'; id: string }
  ): Promise<NetworkPointAssociation | null> => {
    if (!projectId || !entityId || !coordinates) return null;

    setIsLoading(true);
    setError(null);

    try {
      const result = await pointAssociationApi.addConnection(
        projectId,
        entityType,
        entityId,
        target,
        coordinates,
      );
      await loadAssociations();
      // Limpiar candidatos después de agregar
      setCandidates([]);
      return result;
    } catch (err) {
      console.error('[usePointAssociation] Error adding connection:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [projectId, entityType, entityId, coordinates, loadAssociations]);

  /**
   * Eliminar una conexión
   */
  const removeConnection = useCallback(async (connectionId: string): Promise<void> => {
    if (!projectId || !entityId) return;

    setIsLoading(true);
    setError(null);

    try {
      await pointAssociationApi.removeConnection(projectId, entityType, entityId, connectionId);
      await loadAssociations();
    } catch (err) {
      console.error('[usePointAssociation] Error removing connection:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
    } finally {
      setIsLoading(false);
    }
  }, [projectId, entityType, entityId, loadAssociations]);

  /**
   * Establecer conexión como primaria
   */
  const setPrimary = useCallback(async (connectionId: string): Promise<NetworkPointAssociation | null> => {
    if (!projectId || !entityId) return null;

    setIsLoading(true);
    setError(null);

    try {
      const result = await pointAssociationApi.setPrimary(projectId, entityType, entityId, connectionId);
      await loadAssociations();
      return result;
    } catch (err) {
      console.error('[usePointAssociation] Error setting primary:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [projectId, entityType, entityId, loadAssociations]);

  /**
   * Cargar candidatos para conexión manual
   */
  const loadCandidates = useCallback(async (options?: {
    maxDistance?: number;
    includeNodes?: boolean;
  }): Promise<void> => {
    if (!projectId || !coordinates) return;

    setIsLoadingCandidates(true);

    try {
      const results = await pointAssociationApi.getCandidates(
        projectId,
        entityType,
        coordinates,
        options,
      );
      // Filtrar candidatos que ya están conectados
      const connectedIds = new Set(allAssociations.map((a) =>
        a.associationType === 'edge' ? a.edgeId : a.nodeId
      ));
      const filtered = results.filter((c) => !connectedIds.has(c.id));
      setCandidates(filtered);
    } catch (err) {
      console.error('[usePointAssociation] Error loading candidates:', err);
    } finally {
      setIsLoadingCandidates(false);
    }
  }, [projectId, entityType, coordinates, allAssociations]);

  /**
   * Limpiar candidatos
   */
  const clearCandidates = useCallback(() => {
    setCandidates([]);
  }, []);

  /**
   * Recargar desde el servidor
   */
  const refresh = useCallback(async () => {
    await loadAssociations();
  }, [loadAssociations]);

  return {
    association,
    allAssociations,
    candidates,
    isLoading,
    isLoadingCandidates,
    error,
    allowsMultipleConnections,
    recalculate,
    lock,
    unlock,
    lockConnection,
    unlockConnection,
    addConnection,
    replacePrimary,
    removeConnection,
    setPrimary,
    loadCandidates,
    clearCandidates,
    refresh,
  };
}
